/**
 * Dual-Device Controls Component
 * Manages linking two M-Scale devices for 32-scale operation
 */

import { useState } from 'react'
import { useMScaleStore } from '../../store'
import { bluetoothService } from '../../services/bluetooth/BluetoothService'
import { Card } from '../Common/Card'
import { Button } from '../Common/Button'
import type { DeviceLinkConfig } from '../../services/bluetooth/types'

export function DualDeviceControls() {
  const {
    isDualDeviceMode,
    deviceLinkStatus,
    layoutConfig,
    deviceName,
    linkSecondaryDevice,
    unlinkSecondaryDevice,
    setLayoutConfig,
  } = useMScaleStore()

  const [isScanning, setIsScanning] = useState(false)
  const [availableDevices, setAvailableDevices] = useState<BluetoothDevice[]>([])
  const [showDevicePicker, setShowDevicePicker] = useState(false)
  const [isLinking, setIsLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleScanForDevices = async () => {
    try {
      setIsScanning(true)
      setError(null)
      const devices = await bluetoothService.scanForDevices()
      setAvailableDevices(devices)
      setShowDevicePicker(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scan for devices')
      console.error('Failed to scan for devices:', err)
    } finally {
      setIsScanning(false)
    }
  }

  const handleSelectDevice = async (device: BluetoothDevice) => {
    try {
      setIsLinking(true)
      setError(null)

      const deviceInfo = bluetoothService.getDeviceInfo(device)

      // Check if user selected the same device as primary
      if (deviceInfo.name === deviceName) {
        const confirmSame = window.confirm(
          `Warning: You selected "${deviceInfo.name}" which appears to be the currently connected primary device.\n\n` +
          `This will not work - you need to select a DIFFERENT M-Scale device as the secondary.\n\n` +
          `Do you want to continue anyway?`
        )
        if (!confirmSame) {
          setIsLinking(false)
          return
        }
      }

      // TODO: Add firmware version check here
      // const secondaryVersion = await getSecondaryFirmwareVersion(device)
      // if (secondaryVersion !== firmwareVersion) {
      //   const confirm = window.confirm('Firmware versions do not match. Continue anyway?')
      //   if (!confirm) return
      // }

      await linkSecondaryDevice(deviceInfo.id, deviceInfo.name)
      setShowDevicePicker(false)
      setAvailableDevices([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link device')
      console.error('Failed to link device:', err)
    } finally {
      setIsLinking(false)
    }
  }

  const handleUnlink = async () => {
    try {
      setError(null)
      await unlinkSecondaryDevice()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink device')
      console.error('Failed to unlink device:', err)
    }
  }

  const handleLayoutChange = async (updates: Partial<DeviceLinkConfig>) => {
    try {
      setError(null)
      const newConfig = { ...layoutConfig, ...updates }
      setLayoutConfig(newConfig)

      // Send config to device
      await bluetoothService.setDeviceLinkConfig(newConfig)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update layout')
      console.error('Failed to update layout:', err)
    }
  }

  return (
    <Card title="Dual-Device Mode">
      <div className="space-y-4">
        {/* Status Display */}
        {isDualDeviceMode && deviceLinkStatus ? (
          <div className="p-3 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">Linked to Secondary Device</div>
                <div className="text-sm mt-1">
                  {deviceLinkStatus.linkedDeviceName} (v{deviceLinkStatus.linkedFirmwareVersion})
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={handleUnlink}>
                Unlink
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Single device mode (16 scales)
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Link Device Button */}
        {!isDualDeviceMode && (
          <div>
            <Button
              onClick={handleScanForDevices}
              disabled={isScanning}
              className="w-full"
            >
              {isScanning ? 'Scanning...' : 'Link Secondary Device'}
            </Button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Connect a second M-Scale to enable 32-scale operation
            </p>
          </div>
        )}

        {/* Device Picker Modal */}
        {showDevicePicker && (
          <div className="space-y-2">
            <div className="text-sm font-semibold">Select Secondary Device:</div>
            {availableDevices.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                No devices found. Make sure the secondary device is powered on and advertising.
              </div>
            ) : (
              <div className="space-y-2">
                {availableDevices.map((device, index) => {
                  const info = bluetoothService.getDeviceInfo(device)
                  return (
                    <button
                      key={index}
                      onClick={() => handleSelectDevice(device)}
                      disabled={isLinking}
                      className="w-full p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-left transition-colors disabled:opacity-50"
                    >
                      <div className="font-medium">{info.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        ID: {info.id}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowDevicePicker(false)
                setAvailableDevices([])
              }}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Layout Configuration */}
        {isDualDeviceMode && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
            <h3 className="font-semibold text-sm">Layout Configuration</h3>

            {/* Relative Position */}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                Secondary Position
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['right', 'left', 'below', 'above'] as const).map((pos) => (
                  <button
                    key={pos}
                    onClick={() => handleLayoutChange({ relativePosition: pos })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      layoutConfig.relativePosition === pos
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {pos.charAt(0).toUpperCase() + pos.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Primary Rotation */}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                Primary Rotation
              </label>
              <div className="grid grid-cols-4 gap-2">
                {([0, 90, 180, 270] as const).map((rotation) => (
                  <button
                    key={rotation}
                    onClick={() => handleLayoutChange({ primaryRotation: rotation })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      layoutConfig.primaryRotation === rotation
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {rotation}°
                  </button>
                ))}
              </div>
            </div>

            {/* Secondary Rotation */}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                Secondary Rotation
              </label>
              <div className="grid grid-cols-4 gap-2">
                {([0, 90, 180, 270] as const).map((rotation) => (
                  <button
                    key={rotation}
                    onClick={() => handleLayoutChange({ secondaryRotation: rotation })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      layoutConfig.secondaryRotation === rotation
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {rotation}°
                  </button>
                ))}
              </div>
            </div>

            {/* Visual Preview */}
            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Layout Preview
              </div>
              <LayoutPreview config={layoutConfig} />
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Simple visual preview of the layout configuration
 */
function LayoutPreview({ config }: { config: DeviceLinkConfig }) {
  const { relativePosition, primaryRotation, secondaryRotation } = config

  const getFlexDirection = () => {
    if (relativePosition === 'right' || relativePosition === 'left') {
      return relativePosition === 'right' ? 'flex-row' : 'flex-row-reverse'
    }
    return relativePosition === 'below' ? 'flex-col' : 'flex-col-reverse'
  }

  const getRotationClass = (rotation: number) => {
    return `rotate-${rotation === 0 ? '0' : rotation === 90 ? '90' : rotation === 180 ? '180' : '270'}`
  }

  return (
    <div className={`flex ${getFlexDirection()} gap-2 items-center justify-center py-2`}>
      {/* Primary Device */}
      <div className="flex flex-col items-center gap-1">
        <div className={`w-12 h-12 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold transform ${getRotationClass(primaryRotation)}`}>
          P
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Primary</div>
      </div>

      {/* Secondary Device */}
      <div className="flex flex-col items-center gap-1">
        <div className={`w-12 h-12 bg-purple-500 rounded flex items-center justify-center text-white text-xs font-bold transform ${getRotationClass(secondaryRotation)}`}>
          S
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Secondary</div>
      </div>
    </div>
  )
}
